/* eslint-disable react-hooks/exhaustive-deps */
import styled from '@emotion/styled';
import * as React from 'react';
import { Spacings } from '@styles/index';
import { Popper } from '@material-ui/core';
import Dialog from '@comp/Dialog/Dialog';
import { ILesson } from 'types/syllabus';
import { useDispatch, useSelector } from 'react-redux';
import { removeEntityFromSaved } from '@actions/saved';
import { useRouter } from 'next/router';
import { segment } from '@actions/analytics';
import { SAVE_UNSAVE_CONTENT, TOPIC_SYLLABUS_PAGE } from '@constants/segment';
import { savedObjectTypes, BACKEND_API_ERROR } from '@constants/index';
import { useSnackbar } from '@stores/snackbar';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { getIsGTPGoal } from '@api/selectors/constants';
import useConstants from '@api/hooks/useConstants';
import LessonMenuPopupContent from './LessonMenuPopupContent';

export interface ILessonMenuPopupProps {
  children: React.ReactNode;
}

export interface ILessonMenuPopupFn {
  handleShowPopup: (event: any, lesson: ILesson) => void;
  handleHidePopup: () => void;
}

const StyPopper = styled(Popper)`
  z-index: 1301;
  margin-left: ${Spacings.SPACING_9B};
`;

const ClickAwayWrapper = styled.div``;

const LessonMenuPopup = React.forwardRef<
  ILessonMenuPopupFn,
  ILessonMenuPopupProps
>(({ children }, ref) => {
  const [open, setOpen] = React.useState(false);
  const [selectedLesson, setSelectedLesson] = React.useState(null);
  const [popupAnchorEl, setPopupAnchorEl] =
    React.useState<HTMLDivElement>(null);
  const [showUnsaveDialog, setShowUnsavedDialog] = React.useState(false);
  React.useImperativeHandle(ref, () => ({ handleShowPopup, handleHidePopup }));
  const dispatch = useDispatch();
  const { query } = useRouter();
  const { goalUID } = query;
  const isGTP = useConstants(getIsGTPGoal(goalUID));
  const entityType = savedObjectTypes.LESSON;
  const goalSegmentData = useGoalEventProps({
    goalUID: goalUID as string
  });
  const { open: showSnackbar } = useSnackbar();

  React.useEffect(() => {
    if (window) {
      window.addEventListener('scroll', () => {
        setOpen(false);
      });
    }
  }, []);

  const handleShowPopup = (event, targetLesson) => {
    event.stopPropagation();
    const updatedOpen = selectedLesson === targetLesson ? !open : true;

    if (updatedOpen) {
      setSelectedLesson(targetLesson);
      setPopupAnchorEl(event.currentTarget);
    }
    setOpen(updatedOpen);
  };

  const handleHidePopup = () => {
    setOpen(false);
    setSelectedLesson(null);
    setPopupAnchorEl(null);
  };

  const hidePopupOnSavedClick = () => {
    setOpen(false);
  };

  const onCloseModal = () => {
    setShowUnsavedDialog(false);
  };

  const onOpenUnsaveModal = () => {
    setShowUnsavedDialog(true);
  };

  const handlePostSave = (res, isSave = false) => {
    const message = isSave
      ? 'Saved Lesson successfully'
      : 'Removed from Saved Lessons successfully';
    if (res?.result) {
      showSnackbar({
        message,
        type: 'success'
      });
    } else {
      showSnackbar({
        message: BACKEND_API_ERROR,
        type: 'error'
      });
    }
  };

  const handleUnsaveItem = () => {
    if (selectedLesson) {
      dispatch(
        removeEntityFromSaved({
          goalUid: goalUID,
          objectUid: selectedLesson.uid,
          entityType,
          key: `${goalUID}-lessons`
        })
      )
        // @ts-ignore
        .then((res) => handlePostSave(res))
        .then(() =>
          segment.track(SAVE_UNSAVE_CONTENT, {
            last_primary_source: TOPIC_SYLLABUS_PAGE,
            last_primary_source_section: 'Topic page - Menu Popup',
            save_content_type: 'Lesson',
            save_content_uid: selectedLesson.uid,
            save_content_title: selectedLesson.title,
            ...goalSegmentData
          })
        );
    }
    onCloseModal();
  };

  return (
    <>
      <ClickAwayWrapper>
        {children}
        <StyPopper
          open={open}
          anchorEl={popupAnchorEl}
          placement="right-start"
          modifiers={{
            flip: {
              enabled: false
            },
            hide: { enabled: true }
          }}
        >
          <LessonMenuPopupContent
            selectedLesson={selectedLesson}
            closePopup={handleHidePopup}
            hidePopupOnSavedClick={hidePopupOnSavedClick}
            onOpenUnsaveModal={onOpenUnsaveModal}
            handlePostSave={handlePostSave}
          />
        </StyPopper>
      </ClickAwayWrapper>
      <Dialog
        onClose={onCloseModal}
        dialogState={showUnsaveDialog}
        heading="Remove from Saved?"
        message={`This will remove the lesson from your saved list. ${
          !isGTP ? 'You can add it back by clicking on the save icon.' : ''
        }`}
        messageWidth="82.5%"
        acceptLabel="Remove"
        rejectLabel="Cancel"
        acceptOnClick={handleUnsaveItem}
        rejectOnClick={onCloseModal}
      />
    </>
  );
});

LessonMenuPopup.displayName = 'LessonMenuPopup';
export default LessonMenuPopup;
